import { CONTENT_VERSION } from './content'

export const ELYON_PRODUCT_ID = 'FIGHTER' as const
export const ELYON_ECOSYSTEM = 'ELYON' as const
export const ELYON_CONTRACT_VERSION = 1 as const
export const ELYON_MODE_RESULT_SCHEMA = 'elyon.mode-result/v1' as const
export const ELYON_UNIVERSE_ID = 'elyon' as const
export const ELYON_RIFT_MODE_ID = 'elyon.rift' as const

export type ElyonLaunchContext = {
  sessionId: string
  accountId: string
  characterId: string
  buildId: string
  startedAt: string
  fighterId?: string
  arenaId?: string
  returnTo?: string
  correlationId?: string
}

export type ElyonModeReward = {
  rewardId: string
  kind: 'cosmetic' | 'lore' | 'quest' | 'currency' | 'entitlement'
  quantity: number
  metadata?: Readonly<Record<string, unknown>>
}

export type ElyonModeEvidence = {
  kind: 'build' | 'match' | 'runtime' | 'server' | 'replay'
  ref: string
}

export type ElyonModeResultEnvelope = {
  schema: typeof ELYON_MODE_RESULT_SCHEMA
  eventId: string
  universeId: typeof ELYON_UNIVERSE_ID
  modeId: typeof ELYON_RIFT_MODE_ID
  buildId: string
  accountId: string
  characterId: string
  startedAt: string
  endedAt: string
  outcome: 'win' | 'loss' | 'draw' | 'completed' | 'aborted'
  rewards: readonly ElyonModeReward[]
  evidence: readonly ElyonModeEvidence[]
}

export type ElyonReturnContext = ElyonModeResultEnvelope

export type ElyonCompletedMatch = {
  winnerFighterId: string
  loserFighterId: string
  arenaId: string
  winnerRounds: number
  loserRounds: number
  mode: 'cpu' | 'local'
  occurredAt: string
}

export type ElyonProductHealth = {
  productId: typeof ELYON_PRODUCT_ID
  contractVersion: typeof ELYON_CONTRACT_VERSION
  contentVersion: string
  ready: boolean
  capabilities: readonly string[]
}

export const ELYON_FIGHTER_CAPABILITIES = [
  'fighter-selection',
  'arena-selection',
  'deterministic-combat',
  'input-buffer',
  'frame-data',
  'impact-feedback',
  'elyon-content-adapter',
  'authored-stage-destruction',
  'mode-result-envelope',
] as const

function nonEmpty(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function validTime(value: string | undefined): value is string {
  return nonEmpty(value) && !Number.isNaN(Date.parse(value))
}

export function validateElyonLaunchContext(input: ElyonLaunchContext): string[] {
  const errors: string[] = []
  if (!nonEmpty(input.sessionId)) errors.push('INVALID_SESSION_ID')
  if (!nonEmpty(input.accountId)) errors.push('INVALID_ACCOUNT_ID')
  if (!nonEmpty(input.characterId)) errors.push('INVALID_CHARACTER_ID')
  if (!nonEmpty(input.buildId) || input.buildId.trim().length < 7) errors.push('INVALID_BUILD_ID')
  if (!validTime(input.startedAt)) errors.push('INVALID_STARTED_AT')
  if (input.fighterId !== undefined && !nonEmpty(input.fighterId)) errors.push('INVALID_FIGHTER_ID')
  if (input.arenaId !== undefined && !nonEmpty(input.arenaId)) errors.push('INVALID_ARENA_ID')
  if (input.returnTo !== undefined && !nonEmpty(input.returnTo)) errors.push('INVALID_RETURN_TO')
  if (input.correlationId !== undefined && !nonEmpty(input.correlationId)) errors.push('INVALID_CORRELATION_ID')
  return errors
}

export function validateModeResultEnvelope(input: ElyonModeResultEnvelope): string[] {
  const errors: string[] = []
  if (input.schema !== ELYON_MODE_RESULT_SCHEMA) errors.push('INVALID_SCHEMA')
  if (!nonEmpty(input.eventId) || input.eventId.trim().length < 8) errors.push('INVALID_EVENT_ID')
  if (input.universeId !== ELYON_UNIVERSE_ID) errors.push('INVALID_UNIVERSE_ID')
  if (input.modeId !== ELYON_RIFT_MODE_ID) errors.push('INVALID_MODE_ID')
  if (!nonEmpty(input.buildId) || input.buildId.trim().length < 7) errors.push('INVALID_BUILD_ID')
  if (!nonEmpty(input.accountId)) errors.push('INVALID_ACCOUNT_ID')
  if (!nonEmpty(input.characterId)) errors.push('INVALID_CHARACTER_ID')
  if (!validTime(input.startedAt)) errors.push('INVALID_STARTED_AT')
  if (!validTime(input.endedAt)) errors.push('INVALID_ENDED_AT')
  if (validTime(input.startedAt) && validTime(input.endedAt) && Date.parse(input.endedAt) < Date.parse(input.startedAt)) {
    errors.push('ENDED_BEFORE_STARTED')
  }
  if (!['win', 'loss', 'draw', 'completed', 'aborted'].includes(input.outcome)) errors.push('INVALID_OUTCOME')

  for (const reward of input.rewards) {
    if (!nonEmpty(reward.rewardId)) errors.push('INVALID_REWARD_ID')
    if (!['cosmetic', 'lore', 'quest', 'currency', 'entitlement'].includes(reward.kind)) errors.push(`INVALID_REWARD_KIND:${reward.rewardId || 'unknown'}`)
    if (!Number.isFinite(reward.quantity) || reward.quantity < 0) errors.push(`INVALID_REWARD_QUANTITY:${reward.rewardId || 'unknown'}`)
  }
  for (const evidence of input.evidence) {
    if (!['build', 'match', 'runtime', 'server', 'replay'].includes(evidence.kind)) errors.push('INVALID_EVIDENCE_KIND')
    if (!nonEmpty(evidence.ref)) errors.push('INVALID_EVIDENCE_REF')
  }
  return errors
}

export function createElyonProductHealth(ready = true): ElyonProductHealth {
  return {
    productId: ELYON_PRODUCT_ID,
    contractVersion: ELYON_CONTRACT_VERSION,
    contentVersion: CONTENT_VERSION,
    ready,
    capabilities: ELYON_FIGHTER_CAPABILITIES,
  }
}

export function createReturnContext(
  launch: ElyonLaunchContext,
  outcome: ElyonReturnContext['outcome'],
  endedAt: string,
  evidence: readonly ElyonModeEvidence[] = [],
): ElyonReturnContext {
  const errors = validateElyonLaunchContext(launch)
  if (errors.length) throw new Error(`Invalid Elyon launch context: ${errors.join(',')}`)
  if (!validTime(endedAt)) throw new Error('Invalid Elyon return context: INVALID_ENDED_AT')

  const envelope: ElyonReturnContext = {
    schema: ELYON_MODE_RESULT_SCHEMA,
    eventId: `${launch.sessionId}:rift-result`,
    universeId: ELYON_UNIVERSE_ID,
    modeId: ELYON_RIFT_MODE_ID,
    buildId: launch.buildId,
    accountId: launch.accountId,
    characterId: launch.characterId,
    startedAt: launch.startedAt,
    endedAt,
    outcome,
    // FIGHTER never mints portable Elyon rewards. The universe authority may
    // grant them after validating this evidence envelope.
    rewards: [],
    evidence,
  }
  const envelopeErrors = validateModeResultEnvelope(envelope)
  if (envelopeErrors.length) throw new Error(`Invalid Elyon mode result envelope: ${envelopeErrors.join(',')}`)
  return envelope
}

/**
 * Produces the immutable Fighter -> Elyon handoff after a completed match.
 * Transport, persistence and reward application deliberately remain outside
 * this repository's authority.
 */
export function createCompletedMatchReturnContext(
  launch: ElyonLaunchContext,
  match: ElyonCompletedMatch,
): ElyonReturnContext {
  if (!nonEmpty(match.winnerFighterId)) throw new Error('Invalid completed match: INVALID_WINNER_FIGHTER_ID')
  if (!nonEmpty(match.loserFighterId)) throw new Error('Invalid completed match: INVALID_LOSER_FIGHTER_ID')
  if (match.winnerFighterId.trim() === match.loserFighterId.trim()) throw new Error('Invalid completed match: IDENTICAL_FIGHTERS')
  if (!nonEmpty(match.arenaId)) throw new Error('Invalid completed match: INVALID_ARENA_ID')
  if (!validTime(match.occurredAt)) throw new Error('Invalid completed match: INVALID_OCCURRED_AT')
  if (!Number.isSafeInteger(match.winnerRounds) || match.winnerRounds < 1) throw new Error('Invalid completed match: INVALID_WINNER_ROUNDS')
  if (!Number.isSafeInteger(match.loserRounds) || match.loserRounds < 0 || match.loserRounds >= match.winnerRounds) throw new Error('Invalid completed match: INVALID_LOSER_ROUNDS')

  const matchRef = [
    match.winnerFighterId,
    match.loserFighterId,
    match.arenaId,
    `${match.winnerRounds}-${match.loserRounds}`,
    match.mode,
  ].join('|')

  return createReturnContext(launch, 'win', match.occurredAt, [
    { kind: 'build', ref: launch.buildId },
    { kind: 'match', ref: matchRef },
  ])
}
